import { useEffect, useState } from 'react'
import { FlaskConical, Loader2, Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type {
  InboxCondition,
  InboxConditionField,
  InboxMatchMode,
  InboxRule,
  InboxRuleInput,
} from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

/** Operators per field — mirrors `inboxConditionSchema`. */
const OPS: Record<InboxConditionField, InboxCondition['op'][]> = {
  from: ['contains', 'equals', 'ends_with'],
  subject: ['contains', 'starts_with', 'equals'],
  body: ['contains'],
  to: ['contains'],
  has_attachment: ['is'],
  attachment_name: ['contains', 'ends_with'],
}
const FIELDS = Object.keys(OPS) as InboxConditionField[]

/** Editor row: the value is a string for text fields and a boolean for has_attachment. */
interface DraftCondition {
  field: InboxConditionField
  op: string
  value: string | boolean
}

function toDraft(c: InboxCondition): DraftCondition {
  return { field: c.field, op: c.op, value: c.value }
}

function fromDraft(c: DraftCondition): InboxCondition | null {
  if (c.field === 'has_attachment')
    return { field: 'has_attachment', op: 'is', value: c.value === true }
  const value = typeof c.value === 'string' ? c.value.trim() : ''
  if (!value) return null
  return { field: c.field, op: c.op, value } as InboxCondition
}

function emptyCondition(field: InboxConditionField = 'from'): DraftCondition {
  return {
    field,
    op: OPS[field][0],
    value: field === 'has_attachment' ? true : '',
  }
}

export interface RuleEditorProps {
  projectId: string
  open: boolean
  rule: InboxRule | null
  onOpenChange: (open: boolean) => void
  onSave: (input: InboxRuleInput, ruleId: string | null) => Promise<void>
}

export function RuleEditor({ projectId, open, rule, onOpenChange, onSave }: RuleEditorProps) {
  const { t } = useTranslation('inbox')
  const [name, setName] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [conditions, setConditions] = useState<DraftCondition[]>([emptyCondition()])
  const [matchMode, setMatchMode] = useState<InboxMatchMode>('all')
  const [prompt, setPrompt] = useState('')
  const [autoArchive, setAutoArchive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<Awaited<
    ReturnType<typeof orpc.inbox.testRule>
  > | null>(null)

  useEffect(() => {
    if (!open) return
    setName(rule?.name ?? '')
    setEnabled(rule?.enabled ?? true)
    setConditions(rule?.conditions.length ? rule.conditions.map(toDraft) : [emptyCondition()])
    setMatchMode(rule?.matchMode ?? 'all')
    setPrompt(rule?.prompt ?? '')
    setAutoArchive(rule?.autoArchive ?? true)
    setTestResult(null)
  }, [open, rule])

  const validConditions = conditions.map(fromDraft).filter((c): c is InboxCondition => c !== null)
  const valid = name.trim().length > 0 && validConditions.length > 0 && prompt.trim().length > 0

  const updateCondition = (index: number, patch: Partial<DraftCondition>) => {
    setConditions((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c
        if (patch.field && patch.field !== c.field) return emptyCondition(patch.field)
        return { ...c, ...patch }
      })
    )
    setTestResult(null)
  }

  const test = async () => {
    if (validConditions.length === 0) {
      toast.error(t('rules.editor.validation'))
      return
    }
    setTesting(true)
    try {
      setTestResult(
        await orpc.inbox.testRule({ projectId, rule: { conditions: validConditions, matchMode } })
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setTesting(false)
    }
  }

  const save = async () => {
    if (!valid) {
      toast.error(t('rules.editor.validation'))
      return
    }
    setSaving(true)
    try {
      await onSave(
        {
          name: name.trim(),
          enabled,
          conditions: validConditions,
          matchMode,
          prompt: prompt.trim(),
          autoArchive,
        },
        rule?.id ?? null
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>
            {rule ? t('rules.editor.editTitle') : t('rules.editor.createTitle')}
          </SheetTitle>
          <SheetDescription>{t('rules.description')}</SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-6 px-6 py-5">
          <div className="space-y-2">
            <Label htmlFor="inbox-rule-name">{t('rules.editor.name')}</Label>
            <Input
              id="inbox-rule-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('rules.editor.namePlaceholder')}
              autoFocus
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="inbox-rule-enabled">{t('rules.editor.enabled')}</Label>
            <Switch id="inbox-rule-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label>{t('rules.editor.conditions')}</Label>
              {conditions.length > 1 && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {t('rules.editor.matchMode')}
                  <Select
                    value={matchMode}
                    onValueChange={(v) => {
                      setMatchMode(v as InboxMatchMode)
                      setTestResult(null)
                    }}
                  >
                    <SelectTrigger className="h-7 w-auto gap-1 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('rules.editor.matchAll')}</SelectItem>
                      <SelectItem value="any">{t('rules.editor.matchAny')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <ul className="space-y-2">
              {conditions.map((c, index) => (
                <li key={index} className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                  <Select
                    value={c.field}
                    onValueChange={(v) =>
                      updateCondition(index, { field: v as InboxConditionField })
                    }
                  >
                    <SelectTrigger className="h-9 w-[150px] shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELDS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {t(`rules.fields.${f}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={c.op} onValueChange={(v) => updateCondition(index, { op: v })}>
                    <SelectTrigger className="h-9 w-[130px] shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OPS[c.field].map((op) => (
                        <SelectItem key={op} value={op}>
                          {t(`rules.ops.${op}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {c.field === 'has_attachment' ? (
                    <Select
                      value={c.value === true ? 'true' : 'false'}
                      onValueChange={(v) => updateCondition(index, { value: v === 'true' })}
                    >
                      <SelectTrigger className="h-9 min-w-0 flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">{t('rules.bool.true')}</SelectItem>
                        <SelectItem value="false">{t('rules.bool.false')}</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={typeof c.value === 'string' ? c.value : ''}
                      onChange={(e) => updateCondition(index, { value: e.target.value })}
                      placeholder={t('rules.editor.valuePlaceholder')}
                      className="h-9 min-w-0 flex-1"
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0"
                    title={t('rules.editor.removeCondition')}
                    disabled={conditions.length === 1}
                    onClick={() => {
                      setConditions((prev) => prev.filter((_, i) => i !== index))
                      setTestResult(null)
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={conditions.length >= 10}
              onClick={() => setConditions((prev) => [...prev, emptyCondition()])}
            >
              <Plus className="h-3.5 w-3.5" />
              {t('rules.editor.addCondition')}
            </Button>
          </div>

          <div className="space-y-2">
            <Label htmlFor="inbox-rule-prompt">{t('rules.editor.prompt')}</Label>
            <Textarea
              id="inbox-rule-prompt"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('rules.editor.promptPlaceholder')}
              rows={6}
            />
            <p className="text-xs text-muted-foreground">{t('rules.editor.promptHint')}</p>
          </div>

          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="inbox-rule-archive">{t('rules.editor.autoArchive')}</Label>
            <Switch
              id="inbox-rule-archive"
              checked={autoArchive}
              onCheckedChange={setAutoArchive}
            />
          </div>

          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => void test()}
              disabled={testing}
            >
              {testing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FlaskConical className="h-3.5 w-3.5" />
              )}
              {t('rules.editor.test')}
            </Button>
            {testResult && (
              <div className="text-xs text-muted-foreground">
                {testResult.matches.length === 0 ? (
                  <p>{t('rules.editor.testEmpty')}</p>
                ) : (
                  <>
                    <p className="font-medium text-foreground">
                      {t('rules.editor.testResult', {
                        count: testResult.matches.length,
                        total: testResult.total,
                      })}
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {testResult.matches.slice(0, 8).map((m) => (
                        <li key={m.id} className="truncate">
                          {m.fromName || m.fromAddress} — {m.subject || t('noSubject')}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <SheetFooter className="border-t px-6 py-4 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('rules.editor.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving || !valid}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('rules.editor.save')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
